import { createPillButton } from './pillButton.js';

export type ConfirmModalOptions = {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Accent destructif sur le bouton de confirmation. */
  danger?: boolean;
};

export type AlertModalOptions = {
  title: string;
  message: string;
  okLabel?: string;
};

/**
 * Modale de confirmation réutilisable (`.modal-backdrop` + `.modal`).
 * Escape / clic backdrop / Annuler → `false` ; Confirmer → `true`.
 */
export function openConfirmModal(
  host: HTMLElement,
  options: ConfirmModalOptions
): Promise<boolean> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop confirm-modal';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');
    backdrop.setAttribute('aria-labelledby', 'confirm-modal-title');

    backdrop.innerHTML = `
      <div class="modal confirm-modal-dialog">
        <div class="modal-header">
          <h2 id="confirm-modal-title"></h2>
          <button type="button" class="btn btn-ghost confirm-modal-close" aria-label="Fermer">×</button>
        </div>
        <div class="modal-body">
          <p class="confirm-modal-message"></p>
        </div>
        <div class="modal-footer confirm-modal-footer"></div>
      </div>`;

    const titleEl = backdrop.querySelector('#confirm-modal-title') as HTMLElement;
    const messageEl = backdrop.querySelector('.confirm-modal-message') as HTMLElement;
    titleEl.textContent = options.title;
    messageEl.textContent = options.message;

    const footer = backdrop.querySelector('.confirm-modal-footer') as HTMLElement;
    const cancelBtn = createPillButton(options.cancelLabel, { variant: 'soft' });
    const confirmBtn = createPillButton(options.confirmLabel, {
      variant: options.danger ? 'soft' : 'cta',
      class: options.danger ? 'circuits-detail-delete-btn' : undefined,
    });
    footer.append(cancelBtn, confirmBtn);

    let settled = false;
    const finish = (value: boolean): void => {
      if (settled) return;
      settled = true;
      backdrop.remove();
      resolve(value);
    };

    cancelBtn.addEventListener('click', () => finish(false));
    confirmBtn.addEventListener('click', () => finish(true));
    backdrop.querySelector('.confirm-modal-close')?.addEventListener('click', () => finish(false));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) finish(false);
    });
    backdrop.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') finish(false);
    });

    host.appendChild(backdrop);
    (cancelBtn as HTMLButtonElement).focus();
  });
}

/**
 * Modale d’information (remplace `window.alert`) — un seul bouton OK.
 * Escape / clic backdrop / OK ferment la modale.
 */
export function openAlertModal(host: HTMLElement, options: AlertModalOptions): Promise<void> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop confirm-modal';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');
    backdrop.setAttribute('aria-labelledby', 'confirm-modal-title');

    backdrop.innerHTML = `
      <div class="modal confirm-modal-dialog">
        <div class="modal-header">
          <h2 id="confirm-modal-title"></h2>
          <button type="button" class="btn btn-ghost confirm-modal-close" aria-label="Fermer">×</button>
        </div>
        <div class="modal-body">
          <p class="confirm-modal-message"></p>
        </div>
        <div class="modal-footer confirm-modal-footer"></div>
      </div>`;

    const titleEl = backdrop.querySelector('#confirm-modal-title') as HTMLElement;
    const messageEl = backdrop.querySelector('.confirm-modal-message') as HTMLElement;
    titleEl.textContent = options.title;
    messageEl.textContent = options.message;

    const footer = backdrop.querySelector('.confirm-modal-footer') as HTMLElement;
    const okBtn = createPillButton(options.okLabel ?? 'OK', { variant: 'cta' });
    footer.append(okBtn);

    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      backdrop.remove();
      resolve();
    };

    okBtn.addEventListener('click', () => finish());
    backdrop.querySelector('.confirm-modal-close')?.addEventListener('click', () => finish());
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) finish();
    });
    backdrop.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') finish();
    });

    host.appendChild(backdrop);
    (okBtn as HTMLButtonElement).focus();
  });
}
